/**
 * I18nProvider — wraps the app and exposes the current locale.
 * Locale is persisted in localStorage (key: 'locale').
 * Default: 'es' (Spanish).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { DEFAULT_LOCALE } from './config'
import { TRANSLATION_REGISTRY } from './registry'
import type { Locale, TranslationRegistry } from './types'

interface I18nContextValue {
  locale:      Locale
  setLocale:   (l: Locale) => void
  registry:    TranslationRegistry
}

const I18nContext = createContext<I18nContextValue | null>(null)

const SUPPORTED: Locale[] = ['es', 'en', 'pt']
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

function detectBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const lang = navigator.language.toLowerCase()
  if (lang.startsWith('pt')) return 'pt'
  if (lang.startsWith('en')) return 'en'
  return 'es'
}

function writeLocaleCookie(l: Locale) {
  document.cookie = `locale=${l}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE)

  // Hydrate: localStorage → cookie → browser language (priority order)
  useEffect(() => {
    const stored = localStorage.getItem('locale') as Locale | null
    if (stored && SUPPORTED.includes(stored)) {
      setLocaleState(stored)
      writeLocaleCookie(stored)
      return
    }
    // First visit — detect from browser and persist
    const detected = detectBrowserLocale()
    setLocaleState(detected)
    localStorage.setItem('locale', detected)
    writeLocaleCookie(detected)
  }, [])

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    localStorage.setItem('locale', l)
    writeLocaleCookie(l)
  }, [])

  return (
    <I18nContext.Provider value={{ locale, setLocale, registry: TRANSLATION_REGISTRY }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18nContext(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18nContext must be used inside I18nProvider')
  return ctx
}
